export interface Me {
  user_id: string;
  keycloak_id: string;
  email: string;
  username: string;
  roles: string[];
  tenant_id: string;
}

export interface Supplier {
  id: string;
  name: string;
  code: string;
}

export interface Part {
  id: string;
  part_number: string;
  description: string | null;
}

/** The five classes a part may carry in the master. */
export type PartClass =
  "rotable" | "repairable" | "expendable" | "consumable" | "standard";

export const PART_CLASSES: PartClass[] = [
  "rotable",
  "repairable",
  "expendable",
  "consumable",
  "standard",
];

/** The full part-master row, as the browse endpoint returns it.
 *
 * `Part` stays minimal because the requirement/demand pickers only need a
 * label; this is the shape the Part Master page reads.  `part_class` is
 * nullable on purpose — unrecorded is a distinct state from any of the five,
 * and it is the one that routes a quote to a human.
 */
export interface PartDetail {
  id: string;
  tenant_id: string;
  part_number: string;
  alternate_part_numbers: string[] | null;
  nsn: string | null;
  description: string;
  ata_chapter: string | null;
  unit_of_measure: string;
  // Should be a `PartClass`, but the column is a free-form string and ERP
  // feeds have written worse — typing it narrowly here would be a claim the
  // data does not support. The UI renders an unrecognised value as-is.
  part_class: string | null;
  is_life_limited: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/** Parts per class across the tenant; unrecorded classes land under `unknown`. */
export type PartClassCoverage = Record<string, number>;

export interface Requirement {
  id: string;
  tenant_id: string;
  part_id: string;
  quantity: number;
  condition_code: string;
  required_by_date: string | null;
  is_aog: boolean;
  station: string | null;
  work_order_ref: string | null;
  notes: string | null;
  status: string;
  created_at: string;
}

export interface RFQ {
  id: string;
  tenant_id: string;
  requirement_id: string;
  rfq_number: string;
  status: string;
  response_window_hours: number;
  dispatched_at: string | null;
  expires_at: string | null;
  closed_at: string | null;
  notes: string | null;
  workflow_run_id: string | null;
  part_number: string | null;
  created_at: string;
  updated_at: string;
}

export interface Quote {
  id: string;
  rfq_id: string;
  supplier_id: string;
  // Resolved supplier display name; may be null if the endpoint didn't join it.
  supplier_name: string | null;
  part_number: string;
  condition_code: string;
  quantity: number;
  unit_price: string;
  currency: string;
  lead_time_days: number | null;
  validity_date: string | null;
  has_trace: boolean;
  notes: string | null;
  status: string;
  // Set at import when the quote is usable but suspect — currently a part
  // number that is not the RFQ's. Autonomous ordering refuses to execute these.
  needs_review: boolean;
  review_reason: string | null;
  // Traceability assessed at ingest against what this part in this condition
  // requires. `incomplete` | `missing` | `conflicting` block an order.
  trace_status?: TraceStatus;
  trace_claim?: TraceClaim | null;
  trace_findings?: TraceFinding[] | null;
  // Unit identity — FAA 8130-3 block 10, "Serial/Batch Number". One serial per
  // unit for serialised parts; one lot for batch-controlled material. Usually
  // absent: a distributor assigns a serial at pick, not at quote.
  serial_numbers?: string[] | null;
  batch_number?: string | null;
  created_at: string;
}

export type TraceStatus =
  | "unassessed"
  | "satisfied"
  | "asserted_unverified"
  | "incomplete"
  | "missing"
  | "conflicting";

export interface TraceClaim {
  raw_text: string | null;
  asserted_cert_types: string[];
  issuing_org: string | null;
  source_channel: string | null;
}

export interface TraceFinding {
  code: string;
  severity: "blocking" | "advisory";
  message: string;
}

// Kept in sync with BLOCKING_TRACE_STATUSES in
// services/api-core/app/services/trace_verification.py.
const BLOCKING_TRACE_STATUSES: TraceStatus[] = [
  "incomplete",
  "missing",
  "conflicting",
];

export function isTraceBlocking(status?: TraceStatus | null): boolean {
  return !!status && BLOCKING_TRACE_STATUSES.includes(status);
}

export interface PagedResponse<T> {
  total: number;
  skip: number;
  limit: number;
  items: T[];
}

export interface AuditEvent {
  id: string;
  tenant_id: string;
  user_id: string | null;
  action: string;
  resource_type: string;
  resource_id: string | null;
  changes:
    { before?: unknown; after?: unknown } | Record<string, unknown> | null;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
  // Resolved email of the acting user; null for system events or deleted users.
  actor_email: string | null;
}

export interface ScoredQuote {
  quote_id: string;
  supplier_id: string;
  supplier_name: string;
  unit_price: number;
  currency: string;
  lead_time_days: number | null;
  has_trace: boolean;
  price_score: number;
  lead_time_score: number;
  trace_score: number;
  overall_score: number;
  analysis: string;
  price_vs_baseline_pct: number | null;
  // Set upstream when the quoted part number is not the one the RFQ asked for.
  needs_review?: boolean;
  review_reason?: string;
  trace_status?: TraceStatus;
  trace_blocking?: boolean;
  trace_summary?: string;
}

export interface AgentDecision {
  id: string;
  tenant_id: string;
  agent_type: string;
  model_used: string;
  input_data: Record<string, unknown>;
  output_data: Record<string, unknown>;
  reasoning: string | null;
  confidence: number | null;
  prompt_tokens: number | null;
  completion_tokens: number | null;
  total_cost: string | null;
  latency_ms: number | null;
  related_entity_type: string | null;
  related_entity_id: string | null;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

/** Acknowledgement from POST /rfqs/{id}/reanalyze — the pipeline runs async. */
export interface ReanalyzeResult {
  workflow_id: string;
  quote_count: number;
  superseded_recommendations: number;
  message: string;
}

export interface Recommendation {
  id: string;
  tenant_id: string;
  rfq_id: string;
  recommended_quote_id: string | null;
  reasoning: string | null;
  summary: string | null;
  confidence: number | null;
  status: string;
  scored_quotes: ScoredQuote[];
  agent_decision_ids: string[];
  actioned_by_user_id: string | null;
  actioned_at: string | null;
  override_quote_id: string | null;
  feedback: string | null;
  created_at: string;
  updated_at: string;
  rfq_status: string | null;
  // True once a PO exists for one of the RFQ's quotes: the recommendation can
  // no longer be approved, rejected, or negotiated.
  rfq_award_locked: boolean;
}

export interface ApproveResponse {
  recommendation: Recommendation;
  po_id: string | null;
  po_number: string | null;
  po_status: string | null;
  po_needs_manager_approval: boolean;
}

/**
 * The traceability an order was placed on, as frozen onto the PO.
 *
 * Only the requirement is modelled here. `trace_commitment` also holds the
 * assessment of the supplier's quote — the verdict, its findings, and who
 * overrode a block — but those belong on the quote screen where the decision
 * was made. Repeating them on the order would invite reading them as terms of
 * the order, which they are not.
 */
export interface TraceRequirement {
  condition_code: string;
  acceptable_cert_types: string[];
  airworthiness_release_required: boolean;
  coc_acceptable: boolean;
  back_to_birth_required: boolean;
  part_class_known: boolean;
  rationale: string;
  caveats: string[];
}

export interface TraceCommitment {
  requirement?: TraceRequirement | null;
  status?: TraceStatus | null;
  acknowledged?: {
    by_user_id: string | null;
    at: string;
    cleared_status: string;
  } | null;
}

/** A difference between what was ordered and what the supplier confirmed. */
export interface AckDiscrepancy {
  field: string;
  ordered: string | number;
  acknowledged: string | number;
}

/**
 * The supplier's answer to a purchase order, as it arrived on the wire.
 *
 * `accepted` is the supplier's decision, not ours: AC and AD take the order
 * on, RJ and RD do not. An accepted order whose `discrepancies` are non-empty
 * is the case that needs a buyer's eye — it was taken on, but not as ordered.
 */
export interface SupplierAck {
  type: string; // "AC" | "AD" | "RJ" | "RD"
  accepted: boolean;
  received_at: string;
  format?: string; // "X12" | "EDIFACT"
  transaction?: string; // "855" | "ORDRSP"
  sender_id?: string;
  notes?: string | null;
  discrepancies?: AckDiscrepancy[];
}

/**
 * One demand record an order was placed to satisfy.
 *
 * Reached through the order's RFQ, not through the order: a consolidated RFQ
 * covers several requirements and one PO against it satisfies all of them, so
 * there is no PO→requirement column on either side.
 */
export interface PORequirement {
  requirement_id: string;
  part_number: string | null;
  condition_code: string | null;
  /** What the RFQ was raised for against this requirement… */
  quantity_covered: number | null;
  /** …and what the requirement itself asks for. Usually equal. */
  quantity_required: number | null;
  status: string;
  is_aog: boolean;
  required_by_date: string | null;
  work_order_ref: string | null;
}

export interface PurchaseOrder {
  id: string;
  tenant_id: string;
  quote_id: string;
  rfq_id: string;
  po_number: string;
  status: string;
  total_amount: string;
  currency: string;
  // The ordered line, snapshotted from the quote at order time. Null on orders
  // placed before migration 022 whose quote is no longer resolvable.
  part_number?: string | null;
  condition_code?: string | null;
  quantity?: number | null;
  unit_price?: string | null;
  unit_of_measure?: string;
  serial_numbers?: string[] | null;
  batch_number?: string | null;
  trace_commitment?: TraceCommitment | null;
  /**
   * The demand behind this order. Populated only by `GET /purchase-orders/{id}`
   * — `null` means "not loaded" (the list route never fetches it), `[]` means
   * the order genuinely covers no requirement.
   */
  requirements?: PORequirement[] | null;
  approved_by_user_id: string | null;
  approved_at: string | null;
  sent_at: string | null;
  erp_po_ref: string | null;
  // How the order reached the supplier, and what each side calls it. Null
  // until dispatch; dispatch_error is set instead when a send failed, and is
  // cleared once a later attempt succeeds.
  dispatch_channel?: string | null;
  supplier_po_ref?: string | null;
  dispatch_error?: string | null;
  // What the supplier said when it acknowledged the order (855/ORDRSP). Null
  // until an acknowledgment arrives.
  supplier_ack?: SupplierAck | null;
  notes: string | null;
  idempotency_key: string | null;
  po_workflow_run_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface Shipment {
  id: string;
  tenant_id: string;
  purchase_order_id: string;
  tracking_number: string | null;
  carrier: string | null;
  status: string;
  shipped_at: string | null;
  estimated_delivery: string | null;
  delivered_at: string | null;
  received_at: string | null;
  /**
   * Units the receipt confirmed. Null until one arrives; below the order
   * quantity means a short shipment — which is why the demand behind a
   * `Received` order can still be open.
   */
  quantity_received: number | null;
  /** See `ReceivingItem.inspection_reopened_at`. */
  inspection_reopened_at: string | null;
  inspection_reopened_reason: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

// ── Receiving inspection (Stage 2) ────────────────────────────────────────────

/** One finding from `app/services/trace_reconciliation.py`. */
export interface Discrepancy {
  code: string;
  severity: "blocking" | "advisory";
  message: string;
  expected?: string | null;
  actual?: string | null;
  /** The one document at fault, where a single document is. */
  document_id?: string | null;
  /**
   * Every document the finding involves — which is a weaker claim than
   * `document_id`. Five certificates that contradict each other implicate all
   * five without any one of them being wrong, and a form the inspector reads
   * on its own must not look clean just because the machine cannot say which
   * of the five to reject.
   */
  document_ids?: string[] | null;
}

/**
 * Block-level data read off a certificate. Every field is optional: this is
 * what a model could find on a scan, not a guaranteed record.
 */
export interface CertificateFields {
  document_type?: string | null;
  form_tracking_number?: string | null;
  issuing_organization?: string | null;
  part_number?: string | null;
  quantity?: number | null;
  serial_number?: string | null;
  status_work?: string | null;
  remarks?: string | null;
  block_13_signed?: boolean;
  block_14_signed?: boolean;
  approval_number?: string | null;
  signature_date?: string | null;
  certifying_staff?: string | null;
  confidence?: number;
}

export interface TraceDocument {
  id: string;
  tenant_id: string;
  quote_id: string | null;
  purchase_order_id: string | null;
  shipment_id: string | null;
  filename: string;
  content_type: string;
  size_bytes: number;
  page_count: number | null;
  sha256: string;
  source_channel: string;
  document_type: string;
  extraction_status: "pending" | "extracted" | "failed";
  extraction_error: string | null;
  extracted_fields: CertificateFields | null;
  created_at: string;
  /**
   * Set aside by an inspector as not belonging to this shipment. The checks
   * skip it, and it stays on screen: disregarding a certificate is part of the
   * record, not a way of removing it from one.
   */
  excluded_at: string | null;
  excluded_by_user_id: string | null;
  exclusion_reason: string | null;
  /** Expires — fetched per response, never stored. */
  download_url: string | null;
}

export type InspectionStatus =
  "pending" | "passed" | "discrepancy" | "quarantined" | "waived";

export type InspectionDecision = "accept" | "quarantine" | "waive";

export interface ReceivingItem {
  shipment_id: string;
  purchase_order_id: string;
  po_number: string;
  part_number: string | null;
  condition_code: string | null;
  quantity: number | null;
  supplier_name: string | null;
  status: string;
  inspection_status: InspectionStatus;
  inspection_findings: Discrepancy[] | null;
  /** What the order was placed on, for the side-by-side comparison. */
  trace_commitment: TraceCommitment | null;
  blocking_count: number;
  /** Documents the checks ran against — excludes any set aside. */
  document_count: number;
  excluded_document_count: number;
  received_at: string | null;
  /** What turned up, against `quantity` ordered. Null before a receipt. */
  quantity_received: number | null;
  inspected_at: string | null;
  /**
   * Who cleared it, when a person did — null while the verdict is the
   * machine's. `accept` writes `passed`, the same value reconciliation writes
   * when it finds nothing wrong, so the status alone cannot tell a clean pack
   * from one a manager released over four blocking findings.
   */
  inspected_by_user_id: string | null;
  inspected_by_email: string | null;
  /**
   * Set while the shipment is back at goods-in because a corrected shipping
   * notice restated what was in the box after it had already been decided. The
   * verdict was surrendered, so without this the row looks like one nobody has
   * ever ruled on. Cleared when a person rules again.
   */
  inspection_reopened_at: string | null;
  inspection_reopened_reason: string | null;
}

export interface TraxWorkOrder {
  work_order_ref: string;
  part_number: string;
  description: string;
  quantity: number;
  condition_code: string;
  required_by_date: string | null;
  is_aog: boolean;
  station: string | null;
  import_status: "new" | "already_imported" | "no_part";
  import_status_reason: string;
  part_description?: string;
}

export interface TraxImportResult {
  imported: number;
  skipped: number;
  total_work_orders: number;
  message: string;
  details: {
    work_order_ref: string;
    part_number: string;
    status: "imported" | "skipped" | "error";
    reason?: string;
    requirement_id?: string;
    rfq_id?: string;
    part_description?: string;
  }[];
}

export interface AnalyticsOverview {
  period_days: number;
  total_spend_usd: number;
  po_count: number;
  rfq_total: number;
  rfq_quoted: number;
  rfq_expired: number;
  rfq_conversion_rate: number;
  avg_savings_pct: number;
}

export interface SpendBySupplier {
  supplier_id: string;
  supplier_name: string;
  supplier_code: string;
  po_count: number;
  total_spend: number;
  avg_po_value: number;
  currency: string;
}

export interface SpendMonthly {
  month: string;
  po_count: number;
  total_spend: number;
}

export interface SpendByCategory {
  ata_chapter: string;
  po_count: number;
  total_spend: number;
}

export interface RFQMetrics {
  period_days: number;
  total: number;
  quoted: number;
  expired: number;
  in_progress: number;
  conversion_rate: number;
  avg_dispatch_hours: number;
  avg_response_hours: number;
}

export interface AOGMetrics {
  period_days: number;
  total_aog_requirements: number;
  avg_first_quote_hours: number;
  min_first_quote_hours: number;
  max_first_quote_hours: number;
}

export interface SavingsMetrics {
  period_days: number;
  approved_recommendations: number;
  avg_savings_pct: number;
  estimated_savings_usd: number;
  savings_direction: "below_market" | "above_market";
}

export interface LeadTimeBySupplier {
  supplier_id: string;
  supplier_name: string;
  avg_lead_time_days: number;
  min_lead_time_days: number;
  max_lead_time_days: number;
  quote_count: number;
}

export interface SupplierScorecard {
  supplier_id: string;
  supplier_name: string;
  supplier_code: string;
  country: string | null;
  responsiveness_hours: number;
  price_competitiveness: number;
  on_time_delivery_rate: number;
  score_overall: number;
  scorecard_updated_at: string | null;
}

export interface PdfQuoteUploadResult {
  accepted: boolean;
  quote_id: string | null;
  message: string;
  extraction_method: string;
  confidence: number;
  part_number: string | null;
  unit_price: number | null;
}

export interface ConsolidationGroup {
  id: string;
  tenant_id: string;
  part_id: string;
  condition_code: string;
  window_start: string;
  window_end: string;
  requirement_ids: string[];
  total_quantity: number;
  station_codes: string[];
  status: string;
  consolidated_rfq_id: string | null;
  reasoning: string | null;
  potential_savings_pct: number | null;
  agent_decision_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface VendorOptimizationRun {
  id: string;
  tenant_id: string;
  status: string;
  analysis_period_days: number;
  recommendations: VendorRecommendation[];
  reasoning: string | null;
  generated_at: string | null;
  actioned_by_user_id: string | null;
  actioned_at: string | null;
  agent_decision_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface VendorRecommendation {
  category: string;
  from_supplier_id: string;
  from_supplier_name: string;
  to_supplier_id: string;
  to_supplier_name: string;
  recommended_shift_pct: number;
  current_spend_usd: number;
  expected_savings_usd: number;
  rationale: string;
}

export interface Negotiation {
  id: string;
  tenant_id: string;
  rfq_id: string;
  quote_id: string;
  supplier_id: string;
  status: string;
  current_round: number;
  max_rounds: number;
  price_floor_pct: number;
  initial_unit_price: number;
  current_best_price: number | null;
  transcript: NegotiationTurn[];
  final_price: number | null;
  final_terms: Record<string, unknown> | null;
  pending_approval: boolean;
  pending_approval_amount: number | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  // Present once the negotiation has been accepted and turned into a PO.
  po_id: string | null;
  po_number: string | null;
  po_status: string | null;
}

export interface NegotiationTurn {
  role: "agent" | "supplier";
  content: string;
  round: number;
  proposed_price: number | null;
  timestamp: string;
}

// Phase 9 — Autonomous Ordering

export interface AutonomousPolicy {
  id: string;
  tenant_id: string;
  enabled: boolean;
  eligible_categories: string[];
  spend_ceiling_per_transaction_usd: number;
  spend_ceiling_per_period_usd: number;
  period_days: number;
  confidence_threshold: number;
  approved_supplier_ids: string[];
  itar_block_enabled: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface AutonomousOrderLog {
  id: string;
  tenant_id: string;
  recommendation_id: string | null;
  purchase_order_id: string | null;
  policy_snapshot: Record<string, unknown>;
  evaluation_result: Record<string, unknown>;
  executed_autonomously: boolean;
  route_reason: string;
  anomaly_flagged: boolean;
  anomaly_details: Record<string, unknown> | null;
  notified_at: string | null;
  notified_user_id: string | null;
  created_at: string;
}

export interface PagedAutonomousLog {
  total: number;
  skip: number;
  limit: number;
  items: AutonomousOrderLog[];
}

export interface KillSwitchResponse {
  enabled: boolean;
  message: string;
}

export interface StrictResidencyResponse {
  tenant_id: string;
  strict_data_residency: boolean;
}

// Administration — the 5 realm roles (see infra/keycloak/airproc-realm.json)
export type UserRoleName =
  "buyer" | "planner" | "manager" | "finance" | "admin";

export const ALL_ROLES: UserRoleName[] = [
  "buyer",
  "planner",
  "manager",
  "finance",
  "admin",
];

export interface User {
  id: string;
  tenant_id: string;
  keycloak_id: string;
  email: string;
  username: string;
  first_name: string;
  last_name: string;
  role: string;
  roles: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface UserCreatePayload {
  keycloak_id: string;
  email: string;
  username: string;
  first_name?: string;
  last_name?: string;
  roles: UserRoleName[];
  is_active?: boolean;
}

// Dispatch channel the RFQ workflow uses to reach a supplier.
// Mirrors app/adapters/transport.py::TRANSPORTS.
export type SupplierTransport = "rest" | "email" | "edi_x12" | "edi_edifact";

// Full supplier record returned by the CRUD endpoints (the list `Supplier`
// type above is the trimmed shape used by dropdowns).
export interface SupplierDetail {
  id: string;
  tenant_id: string;
  name: string;
  code: string;
  contact_email: string;
  contact_phone: string | null;
  country: string | null;
  certifications: string[] | null;
  is_active: boolean;
  transport: SupplierTransport;
  // Delivery address for the edi_* transports; null for every other channel.
  edi_endpoint: string | null;
  avg_quote_turnaround_hours: number | null;
  on_time_delivery_rate: number | null;
  price_competitiveness_score: number | null;
  created_at: string;
  updated_at: string;
}

export interface SupplierCreatePayload {
  name: string;
  code: string;
  contact_email: string;
  contact_phone?: string | null;
  country?: string | null;
  certifications?: string[] | null;
  is_active?: boolean;
  transport?: SupplierTransport;
  edi_endpoint?: string | null;
}

export type SupplierUpdatePayload = Partial<
  Omit<SupplierCreatePayload, "code">
>;

export type ConnectorHealthStatus = "healthy" | "degraded" | "down" | "unknown";

export interface ConnectorHealthSummary {
  connector_name: string;
  connector_type: string;
  is_enabled: boolean;
  status: ConnectorHealthStatus;
  last_success_at: string | null;
  calls_24h: number;
  errors_24h: number;
  error_rate_24h: number;
  avg_latency_ms_24h: number | null;
  calls_7d: number;
  errors_7d: number;
}

export interface ConnectorConfig {
  id: string;
  tenant_id: string;
  connector_name: string;
  connector_type: string;
  is_enabled: boolean;
  base_url: string | null;
  config_json: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface ConnectorConfigCreatePayload {
  connector_name: string;
  connector_type: string;
  is_enabled?: boolean;
  base_url?: string | null;
  config_json?: Record<string, unknown>;
}

export interface ConnectorConfigUpdatePayload {
  is_enabled?: boolean;
  base_url?: string | null;
  config_json?: Record<string, unknown>;
}

export interface LiveHealthResult {
  connector_name: string;
  reachable: boolean;
  latency_ms: number | null;
  status_detail: string;
}
